import React, { memo } from 'react';
import ReactSelect, { components } from 'react-select';

import { NODATAFOUND } from '../../constants/messageConst';
import bn from '../../generalUtils/benames';
import { DARK_MODE, getLightness, LIGHT_MODE } from '../../generalUtils/themeClasses';
import withRoutingProps from '../../hooks/withRoutingProps';

const bemClass = bn.create('customSelect');

/** -------- PROPERTIES --------
  <CustomSelect
    isLoaded={true} // optional (Pass to dispaly loading message)
    options={optionsList}
    value={selectedValue}
    onChange={handleSelection}
    onBlur={handleBlur} // focus out event
    isClearable={false}
    isDisabled={false}
    isSearchable={true}
    placeholder={'Select...'}
    menuPlacement="auto" // bottom, auto or top
    isMulti={false}
    size = 'sm' // sm, md
    className=""
    containerClass="" // Class for parent div
    isSeparatorVisible={false} // default is true
  />;
*/

const CustomMenuList = props => {
  const { children, getValue, selectProps } = props;
  const { isLoaded, loadingMessage } = selectProps;

  const [selectedValue] = getValue();
  const selectedOptionIndex = props.options.findIndex(option => option.value === selectedValue?.value);

  const menuListRef = React.useRef(null);
  React.useEffect(() => {
    if (menuListRef.current && selectedOptionIndex > -1) {
      const selectedOptionElement = menuListRef.current.children[selectedOptionIndex];
      if (selectedOptionElement) {
        menuListRef.current.scrollTop = selectedOptionElement.offsetTop;
      }
    }
  }, [menuListRef, selectedOptionIndex]);

  if (!isLoaded) {
    return (
      <div className="py-2">
        <i className="fa fa-spinner fa-pulse mx-2" />
        {loadingMessage}
      </div>
    );
  }

  return (
    <components.MenuList {...props} innerRef={menuListRef}>
      {children}
    </components.MenuList>
  );
};

const Control = ({ children, ...props }) => {
  const { beforeInputText } = props.selectProps;

  return (
    <components.Control {...props}>
      {beforeInputText && beforeInputText}
      {children}
    </components.Control>
  );
};

const CustomSelect = props => {
  const {
    id,
    isLoaded = true,
    loadingMessage = 'Loading...',
    options,
    selected,
    onChange,
    onBlur,
    isClearable,
    isDisabled,
    isSearchable,
    placeholder,
    menuPlacement,
    isSeparatorVisible = true,
    isMulti,
    size,
    className,
    containerClass = '',
    containerStyle = {},
    index,
    themeColorObj,
    isLightTheme,
    mode,
    menuPortalTarget,
    beforeInputText,
    dropdownIndicatorIcon = null, // ex. 'fa-bars',
    containerMaxWidth = null,
    menuWidth = null,
    optionsMaxHeight = null
  } = props;

  const FONT_CLR = themeColorObj?.white;
  const FONT_CLR_DISABLED = themeColorObj?.['gray-500'];
  const FONT_CLR_SELECTED = getLightness({ color: themeColorObj?.primary, mode });
  const FONT_CLR_FOCUS = themeColorObj?.['dark-900'];
  const FONT_CLR_HOVER = themeColorObj?.dark;
  const FONT_CLR_ERROR = themeColorObj?.danger;

  const FONT_COLOR_DROPDOWN = themeColorObj?.['dark-100'];
  const FONT_COLOR_PLACEHOLDER = themeColorObj?.['gray-600'];

  const BG_CLR = themeColorObj?.[`${isLightTheme ? 'dark-400' : 'gray-1000'}`];
  const BG_CLR_MULTI = themeColorObj?.[`${isLightTheme ? 'dark-300' : 'dark-400'}`];
  const BG_CLR_SELECTED = themeColorObj?.primary;
  const BG_CLR_FOCUS = themeColorObj?.white;
  const BG_SEPARATOR = themeColorObj?.[isLightTheme ? LIGHT_MODE : DARK_MODE];

  const CustomStyles = (isMulti, size) => {
    const container = base => {
      const containerStyles = {
        ...base,
        height: isMulti ? 'auto' : '100%'
      };
      return containerStyles;
    };

    const control = base => {
      const controlStyles = {
        ...base,
        height:
          size === 'md'
            ? '35.59px !important'
            : size === 'sm'
            ? '31px !important'
            : isMulti
            ? 'inherit !important'
            : '100%',
        minHeight: isMulti ? 'inherit' : '100%',
        maxHeight: isMulti ? 'auto' : '100%',
        width: 'inherit',
        '::-webkit-scrollbar': { width: '.25rem' },
        '::-webkit-scrollbar-track': { backgroundColor: themeColorObj?.['secondary-900'] },
        '::-webkit-scrollbar-thumb': { backgroundColor: themeColorObj?.['secondary-600'] },
        border: `1px solid ${themeColorObj?.['secondary-900']}`,
        boxShadow: 'none',
        borderRadius: '0.25rem',
        cursor: 'pointer',
        fontsize: size === 'md' ? '1rem' : 'inherit',
        backgroundColor: BG_CLR,
        fontWeight: 400
      };

      return controlStyles;
    };

    const option = (styles, state) => {
      const { data, isDisabled, isSelected, isFocused } = state;
      const { customColor = null, customStyle } = data;
      const optionstyles = {
        ...styles,
        cursor: isDisabled ? 'default' : 'pointer !important',
        height: '100%',
        borderBottom: `1px solid ${themeColorObj?.['secondary-600']}`,
        backgroundColor: isSelected ? BG_CLR_SELECTED : isFocused ? BG_CLR_FOCUS : BG_CLR,
        color: isSelected
          ? FONT_CLR_SELECTED
          : customColor
          ? customColor
          : data.isErr
          ? FONT_CLR_ERROR
          : isDisabled
          ? FONT_CLR_DISABLED
          : isFocused
          ? FONT_CLR_FOCUS
          : FONT_CLR,
        fontWeight: isSelected ? 500 : isFocused || isLightTheme ? 400 : 200,
        '&:hover': {
          backgroundColor: isDisabled ? '' : BG_CLR_FOCUS,
          color: isDisabled
            ? ''
            : data.isErr
            ? FONT_CLR_ERROR
            : isFocused
            ? FONT_CLR_HOVER
            : FONT_CLR_SELECTED,
          cursor: isDisabled ? 'default' : 'pointer'
        },
        '&:last-child': {
          borderBottom: 'none'
        },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...customStyle
      };
      return optionstyles;
    };

    const dropdownIndicator = base => {
      const dropdownIndicatorStyles = {
        ...base,
        width: '1.5rem',
        color: FONT_COLOR_DROPDOWN,
        padding: 4,
        '&:hover': { color: FONT_COLOR_DROPDOWN }
      };
      return dropdownIndicatorStyles;
    };

    const placeholder = () => {
      const placeholderStyles = {
        color: `${FONT_COLOR_PLACEHOLDER} !important`,
        fontSize: '0.87rem',
        gridArea: '1/1/1/3',
        paddingLeft: '3px'
      };
      return placeholderStyles;
    };

    const indicatorsContainer = base => {
      const indicatorsContainerStyles = {
        ...base,
        padding: '0'
      };
      return indicatorsContainerStyles;
    };

    const valueContainer = base => {
      const valueContainerStyles = {
        ...base,
        padding: '0 0.3rem',
        maxWidth: containerMaxWidth ?? 'auto'
      };
      return valueContainerStyles;
    };

    const indicatorSeparator = base => {
      const indicatorSeparatorStyles = {
        ...base,
        backgroundColor: BG_SEPARATOR,
        marginBottom: '0.3rem',
        marginTop: '0.35rem',
        display: isSeparatorVisible ? 'flex' : 'none'
      };
      return indicatorSeparatorStyles;
    };

    const menuPortal = base => ({
      ...base,
      zIndex: 999,
      width: menuWidth || base.width
    });

    const menu = base => {
      const menuStyles = {
        ...base,
        background: themeColorObj?.['secondary-900'],
        width: '-webkit-fill-available',
        marginTop: '1px',
        zIndex: '999 !important',
        textAlign: 'left'
      };
      return menuStyles;
    };

    const noOptionsMessage = base => {
      const noOptionsMessageStyles = {
        ...base,
        color: FONT_CLR_DISABLED
      };
      return noOptionsMessageStyles;
    };

    const menuList = base => {
      const menuListStyles = {
        ...base,
        '::-webkit-scrollbar': { width: '.25rem' },
        '::-webkit-scrollbar-track': { backgroundColor: themeColorObj?.['secondary-900'] },
        '::-webkit-scrollbar-thumb': { backgroundColor: themeColorObj?.['secondary-600'] },
        maxHeight: optionsMaxHeight ? optionsMaxHeight : options && options.length > 5 ? '200px' : 'auto'
      };
      return menuListStyles;
    };

    const input = base => {
      const inputStyles = {
        ...base,
        margin: '0',
        padding: '0 3px',
        color: FONT_CLR
      };
      return inputStyles;
    };

    const singleValue = base => {
      const singleValueStyles = {
        ...base,
        color: FONT_CLR,
        padding: '0px 3px'
      };
      return singleValueStyles;
    };

    const multiValue = base => {
      const multiValueStyles = {
        ...base,
        backgroundColor: BG_CLR_MULTI,
        borderRadius: '1rem',
        padding: '0 .3rem',
        margin: '0.35rem 0.35rem 0.35rem 0'
      };
      return multiValueStyles;
    };

    const multiValueLabel = (base, data) => {
      const multiValueLabelStyles = {
        ...base,
        color: data.isErr ? FONT_CLR_ERROR : FONT_CLR
      };
      return multiValueLabelStyles;
    };

    const multiValueRemove = base => {
      const multiValueRemoveStyles = {
        ...base,
        color: FONT_CLR,
        ':hover': {
          backgroundColor: 'transperent',
          color: FONT_CLR_HOVER
        }
      };
      return multiValueRemoveStyles;
    };

    return {
      container,
      control,
      option,
      dropdownIndicator,
      placeholder,
      indicatorsContainer,
      valueContainer,
      indicatorSeparator,
      menuPortal,
      menu,
      noOptionsMessage,
      menuList,
      input,
      singleValue,
      multiValue,
      multiValueLabel,
      multiValueRemove
    };
  };

  const DropdownIndicator = props => {
    return (
      <components.DropdownIndicator {...props}>
        <i className={`fa fa-fw ${dropdownIndicatorIcon}`} />
      </components.DropdownIndicator>
    );
  };

  const ClearIndicator = props => (
    <components.ClearIndicator {...props}>
      <i className="fa fa-times" />
    </components.ClearIndicator>
  );

  return (
    <div
      className={bemClass.b(`${containerClass}`)}
      title={!isMulti ? selected?.label : ''}
      style={containerStyle}
    >
      <ReactSelect
        id={id}
        isLoaded={isLoaded}
        loadingMessage={loadingMessage}
        options={options}
        formatOptionLabel={(data, { context }) =>
          context === 'menu' && data.customLabel ? (
            data.customLabel
          ) : (
            <span title={data.label}>{data.label}</span>
          )
        }
        value={selected}
        onChange={(selection, action) => onChange(selection, action, index)}
        onBlur={onBlur ? selection => onBlur(selection, index) : undefined}
        isClearable={isClearable || false}
        isDisabled={isDisabled || false}
        isSearchable={isSearchable || true}
        placeholder={placeholder || 'Select...'}
        styles={CustomStyles(isMulti, size, className)}
        noOptionsMessage={() => NODATAFOUND}
        closeMenuOnSelect={!isMulti}
        // hideSelectedOptions={false}
        menuPortalTarget={menuPortalTarget}
        menuPlacement={menuPlacement || 'auto'}
        isMulti={isMulti}
        isRtl={false}
        className={className || ''}
        beforeInputText={beforeInputText || ''}
        components={{
          MenuList: CustomMenuList,
          Control,
          ...(isClearable && { ClearIndicator }),
          ...(dropdownIndicatorIcon && { DropdownIndicator })
        }}
        theme={theme => ({
          ...theme,
          spacing: {
            controlHeight: 40,
            menuGutter: 1,
            baseUnit: 2
          }
        })}
      />
    </div>
  );
};

CustomSelect.displayName = 'CustomSelect';
export default memo(withRoutingProps(CustomSelect));
